import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConfig } from '@env/app-config';
import {
  ClientSettingsDTO,
  ClientSettingsUpdateRequest,
  CountryDefaults,
  OwnershipTransferDTO,
  InitiateTransferRequest
} from '../models/configuracion.model';

@Injectable({ providedIn: 'root' })
export class ConfiguracionService {
  private base = AppConfig.apiUrl;
  private settingsUrl  = `${this.base}/api/configuracion`;
  private transferUrl  = `${this.base}/api/configuracion/transferir`;

  constructor(private http: HttpClient) {}

  // ── Configuración ─────────────────────────────────────────────────────────

  getSettings(): Observable<ClientSettingsDTO> {
    return this.http.get<ClientSettingsDTO>(this.settingsUrl);
  }

  createSettings(data: ClientSettingsUpdateRequest): Observable<ClientSettingsDTO> {
    return this.http.post<ClientSettingsDTO>(this.settingsUrl, data);
  }

  updateSettings(data: ClientSettingsUpdateRequest): Observable<ClientSettingsDTO> {
    return this.http.put<ClientSettingsDTO>(this.settingsUrl, data);
  }

  getCountryDefaults(countryCode: string): Observable<CountryDefaults> {
    return this.http.get<CountryDefaults>(`${this.settingsUrl}/paises/defaults/${countryCode}`);
  }

  // ── Transferencia de propiedad ────────────────────────────────────────────

  initiateTransfer(data: InitiateTransferRequest): Observable<OwnershipTransferDTO> {
    return this.http.post<OwnershipTransferDTO>(this.transferUrl, data);
  }

  confirmTransfer(token: string): Observable<OwnershipTransferDTO> {
    return this.http.put<OwnershipTransferDTO>(`${this.transferUrl}/${token}/confirmar`, {});
  }

  cancelTransfer(id: number): Observable<OwnershipTransferDTO> {
    return this.http.put<OwnershipTransferDTO>(`${this.transferUrl}/${id}/cancelar`, {});
  }

  /**
   * Reenvía el correo de solicitud al destinatario. Limitado a un reenvío cada 10 minutos
   * (cooldown forzado en el servidor; el frontend muestra el countdown como mejora de UX).
   */
  resendTransfer(id: number): Observable<OwnershipTransferDTO> {
    return this.http.put<OwnershipTransferDTO>(`${this.transferUrl}/${id}/reenviar`, {});
  }

  rejectTransfer(token: string): Observable<OwnershipTransferDTO> {
    return this.http.put<OwnershipTransferDTO>(`${this.transferUrl}/${token}/rechazar`, {});
  }

  getActivePendingTransfer(): Observable<OwnershipTransferDTO | null> {
    return this.http.get<OwnershipTransferDTO | null>(`${this.transferUrl}/activa`);
  }

  /**
   * Consulta los datos de una solicitud de transferencia pendiente por token,
   * sin modificar su estado. Usado por el resolver de pre-validación de la
   * página de confirmación antes de renderizarla.
   */
  getTransferInfo(token: string): Observable<OwnershipTransferDTO> {
    return this.http.get<OwnershipTransferDTO>(`${this.transferUrl}/${token}/info`);
  }

  getEligibleTransferTargets(): Observable<{ id: number; fullName: string; email: string }[]> {
    return this.http.get<{ id: number; fullName: string; email: string }[]>(
      `${this.transferUrl}/usuarios-elegibles`
    );
  }

  // ── Eliminación de organización ───────────────────────────────────────────

  /**
   * Elimina permanentemente la organización del propietario autenticado.
   * El JWT prueba la identidad; el backend valida isOwner.
   * El frontend debe cerrar sesión inmediatamente tras recibir 200.
   *
   * DELETE /api/configuracion/organizacion
   */
  deleteOrganizacion(): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(
      `${this.settingsUrl}/organizacion`
    );
  }
}
